import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Mail, Search, Eye, Trash2, User, Calendar, AlertCircle,
  Plus, Edit, Filter, X, CheckCircle, Send,
  Clock, UserPlus, Shield, Tag, Key
} from 'lucide-react';
import toast from 'react-hot-toast';
import invitationService from '../services/invitationService';
import dataroomService from '../services/dataroomService';
import roleService from '../services/roleService';
import permissionService from '../services/permissionService';
import tagService from '../services/tagService';
import FilterBar from '../components/FilterBar';
import EnhancedDataTable from '../components/EnhancedDataTable';
import ResponsiveModal, { ResponsiveButton } from '../components/ResponsiveModal';
import { PageLoader } from '../components/CommonLoader';
import { CreateButton } from '../components/CommonButtons';
import SearchableSelect from '../components/SearchableSelect';
import MultiSelectSearchable from '../components/MultiSelectSearchable';

const SuperAdminInvitations = () => {
  const [invitations, setInvitations] = useState([]);
  const [datarooms, setDatarooms] = useState([]);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDataroom, setSelectedDataroom] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [viewMode, setViewMode] = useState('all');
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1
  });

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form states
  const [selectionType, setSelectionType] = useState('permission');
  const [formData, setFormData] = useState({
    email: '',
    dataroom_uuid: '',
    dataroom_role_id: '',
    permission_ids: [],
    tag_ids: [],
    message: '',
    expires_at: ''
  });

  useEffect(() => {
    fetchDatarooms();
  }, []);

  useEffect(() => {
    if (viewMode === 'all') {
      fetchAllInvitations();
    } else if (selectedDataroom) {
      fetchDataroomInvitations();
    }
  }, [pagination.current_page, searchTerm, selectedStatus, selectedDataroom, viewMode]);

  useEffect(() => {
    if (formData.dataroom_uuid) {
      fetchDataroomData(formData.dataroom_uuid);
    }
  }, [formData.dataroom_uuid]);

  const fetchDatarooms = async () => {
    try {
      const response = await dataroomService.getAllDatarooms({ per_page: 100 });
      setDatarooms(response.data || []);
    } catch (error) {
      console.error('Error fetching datarooms:', error);
    }
  };

  const fetchDataroomData = async (uuid) => {
    try {
      // Fetch roles - try super admin endpoint first, fallback to regular
      console.log('Fetching roles for dataroom:', uuid);
      let rolesResponse;
      try {
        rolesResponse = await roleService.superAdminDataroom.list(uuid);
        console.log('Super admin roles response:', rolesResponse);
      } catch (superAdminError) {
        console.log('Super admin endpoint failed, trying regular endpoint:', superAdminError);
        // Fallback to regular endpoint
        rolesResponse = await roleService.dataroom.list(uuid);
        console.log('Fallback regular roles response:', rolesResponse);
      }

      // Extract roles data based on actual API response structure
      let rolesData = [];
      if (rolesResponse.roles && Array.isArray(rolesResponse.roles)) {
        // Direct roles property (from API response) - This is what your API returns
        rolesData = rolesResponse.roles;
      } else if (rolesResponse.data && rolesResponse.data.roles && Array.isArray(rolesResponse.data.roles)) {
        // Nested in data.roles (from service normalization)
        rolesData = rolesResponse.data.roles;
      } else if (Array.isArray(rolesResponse.data)) {
        // Direct data array
        rolesData = rolesResponse.data;
      } else if (Array.isArray(rolesResponse)) {
        // Direct array response
        rolesData = rolesResponse;
      }

      console.log('Extracted roles data:', rolesData);
      setRoles(rolesData.length > 0 ? rolesData : []);

      // Fetch permissions using dataroom-permissions endpoint
      const permissionsResponse = await permissionService.list();

      // Extract permissions data - API returns direct array wrapped in service response
      let permissionsData = [];
      if (Array.isArray(permissionsResponse.data)) {
        // Service returns {data: [...]} structure - This is what your API returns
        permissionsData = permissionsResponse.data;
      } else if (Array.isArray(permissionsResponse)) {
        // Direct array response
        permissionsData = permissionsResponse;
      } else if (permissionsResponse.permissions && Array.isArray(permissionsResponse.permissions)) {
        // Nested in permissions property
        permissionsData = permissionsResponse.permissions;
      }

      console.log('Extracted permissions data:', permissionsData);
      setPermissions(permissionsData.length > 0 ? permissionsData : []);

      // Fetch tags for the selected dataroom
      const tagsResponse = await tagService.list(uuid);
      console.log('Tags response:', tagsResponse);
      // The response should have a 'tags' property due to normalizeApiResponse with 'tags' dataKey
      const tagsData = tagsResponse.tags || tagsResponse.data || [];
      setTags(Array.isArray(tagsData) ? tagsData : []);
    } catch (error) {
      console.error('Error fetching dataroom data:', error);
      // Set empty arrays on error to prevent undefined issues
      setRoles([]);
      setPermissions([]);
      setTags([]);
    }
  };

  const fetchAllInvitations = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current_page,
        per_page: pagination.per_page,
        ...(searchTerm && { search: searchTerm }),
        ...(selectedStatus && { status: selectedStatus })
      };

      const response = await invitationService.admin.listAll(params);

      if (response && response.invitations && Array.isArray(response.invitations)) {
        setInvitations(response.invitations);

        if (response.pagination) {
          setPagination({
            current_page: response.pagination.current_page,
            per_page: response.pagination.per_page,
            total: response.pagination.total,
            last_page: response.pagination.last_page
          });
        }
      } else {
        setInvitations([]);
        setPagination({
          current_page: 1,
          per_page: 15,
          total: 0,
          last_page: 1
        });
      }
    } catch (error) {
      toast.error('Failed to load invitations');
      console.error('Error fetching invitations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDataroomInvitations = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current_page,
        per_page: pagination.per_page,
        ...(searchTerm && { search: searchTerm }),
        ...(selectedStatus && { status: selectedStatus })
      };

      const response = await invitationService.admin.getByDataroom(selectedDataroom, params);

      if (response && response.invitations && Array.isArray(response.invitations)) {
        setInvitations(response.invitations);

        if (response.pagination) {
          setPagination({
            current_page: response.pagination.current_page,
            per_page: response.pagination.per_page,
            total: response.pagination.total,
            last_page: response.pagination.last_page
          });
        }
      } else {
        setInvitations([]);
      }
    } catch (error) {
      toast.error('Failed to load dataroom invitations');
      console.error('Error fetching dataroom invitations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvitation = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.dataroom_uuid) {
      toast.error('Email and dataroom are required');
      return;
    }

    if (selectionType === 'role' && !formData.dataroom_role_id) {
      toast.error('Please select a role');
      return;
    }

    if (selectionType === 'permission' && formData.permission_ids.length === 0) {
      toast.error('Please select at least one permission');
      return;
    }

    setIsSubmitting(true);
    try {
      const dataToSubmit = {
        email: formData.email,
        message: formData.message,
        expires_at: formData.expires_at,
        tag_ids: formData.tag_ids
      };

      // Include either role or permissions based on selection
      if (selectionType === 'role') {
        dataToSubmit.dataroom_role_id = formData.dataroom_role_id;
      } else {
        dataToSubmit.permission_ids = formData.permission_ids;
      }

      await invitationService.admin.createInvitation(formData.dataroom_uuid, dataToSubmit);
      toast.success('Invitation created successfully');
      setShowCreateModal(false);
      resetForm();

      if (viewMode === 'all') {
        fetchAllInvitations();
      } else {
        fetchDataroomInvitations();
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to create invitation';
      toast.error(errorMessage);
      console.error('Error creating invitation:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateInvitation = async (e) => {
    e.preventDefault();

    if (!selectedInvitation) return;

    setIsSubmitting(true);
    try {
      const dataToSubmit = {
        email: formData.email,
        message: formData.message,
        expires_at: formData.expires_at,
        tag_ids: formData.tag_ids
      };

      // Include either role or permissions based on selection
      if (selectionType === 'role') {
        dataToSubmit.dataroom_role_id = formData.dataroom_role_id;
      } else {
        dataToSubmit.permission_ids = formData.permission_ids;
      }

      await invitationService.admin.updateInvitation(
        selectedInvitation.dataroom_uuid,
        selectedInvitation.id,
        dataToSubmit
      );

      toast.success('Invitation updated successfully');
      setShowEditModal(false);
      resetForm();

      if (viewMode === 'all') {
        fetchAllInvitations();
      } else {
        fetchDataroomInvitations();
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update invitation';
      toast.error(errorMessage);
      console.error('Error updating invitation:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteInvitation = async () => {
    if (!selectedInvitation) return;

    setIsSubmitting(true);
    try {
      await invitationService.admin.deleteInvitation(
        selectedInvitation.dataroom_uuid,
        selectedInvitation.id
      );

      toast.success('Invitation deleted successfully');
      setShowDeleteModal(false);
      setSelectedInvitation(null);

      if (viewMode === 'all') {
        fetchAllInvitations();
      } else {
        fetchDataroomInvitations();
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete invitation';
      toast.error(errorMessage);
      console.error('Error deleting invitation:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendInvitation = async (invitation) => {
    try {
      await invitationService.admin.resendInvitation(invitation.dataroom_uuid, invitation.id);
      toast.success('Invitation resent successfully');

      if (viewMode === 'all') {
        fetchAllInvitations();
      } else {
        fetchDataroomInvitations();
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to resend invitation';
      toast.error(errorMessage);
      console.error('Error resending invitation:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      dataroom_uuid: '',
      dataroom_role_id: '',
      permission_ids: [],
      tag_ids: [],
      message: '',
      expires_at: ''
    });
    setSelectionType('role');
    setSelectedInvitation(null);
  };

  const openEditModal = (invitation) => {
    setSelectedInvitation(invitation);
    setFormData({
      email: invitation.email,
      dataroom_uuid: invitation.dataroom_uuid,
      dataroom_role_id: invitation.dataroom_role_id || '',
      permission_ids: invitation.permissions ? invitation.permissions.map(p => p.id) : [],
      tag_ids: invitation.tags ? invitation.tags.map(t => t.id) : [],
      message: invitation.message || '',
      expires_at: invitation.expires_at || ''
    });

    // Determine selection type based on existing data
    if (invitation.dataroom_role_id) {
      setSelectionType('role');
    } else if (invitation.permissions && invitation.permissions.length > 0) {
      setSelectionType('permission');
    }

    setShowEditModal(true);
  };

  const openViewModal = (invitation) => {
    setSelectedInvitation(invitation);
    setShowViewModal(true);
  };

  const openDeleteModal = (invitation) => {
    setSelectedInvitation(invitation);
    setShowDeleteModal(true);
  };

  const getStatusBadge = (status, statusText) => {
    const statusStyles = {
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-green-100 text-green-800',
      expired: 'bg-red-100 text-red-800',
      declined: 'bg-gray-100 text-gray-800',
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      0: 'bg-yellow-100 text-yellow-800', // pending
      1: 'bg-green-100 text-green-800', // accepted
      2: 'bg-yellow-100 text-yellow-800', // pending
      3: 'bg-red-100 text-red-800', // expired
      4: 'bg-gray-100 text-gray-800' // declined
    };

    // Use status_text if available, otherwise fall back to status
    const displayText = statusText || status || 'unknown';

    // Convert status to string and lowercase for style key lookup
    let styleKey = String(status).toLowerCase();

    // Map numeric status to string equivalents for styling
    if (status === 0 || status === '0' || status === 2 || status === '2') {
      styleKey = 'pending';
    } else if (status === 1 || status === '1') {
      styleKey = 'accepted';
    } else if (status === 3 || status === '3') {
      styleKey = 'expired';
    } else if (status === 4 || status === '4') {
      styleKey = 'declined';
    }

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusStyles[styleKey] || 'bg-gray-100 text-gray-800'}`}>
        {displayText}
      </span>
    );
  };

  const columns = [
    {
      key: 'email',
      label: 'Email',
      render: (value, row) => (
        <div className="flex items-center">
          <Mail className="w-4 h-4 mr-2 text-gray-400" />
          <span className="font-medium">{value}</span>
        </div>
      )
    },
    {
      key: 'dataroom_name',
      label: 'Dataroom',
      render: (value, row) => row.dataroom?.name || '-'
    },
    {
      key: 'role',
      label: 'Role/Permissions',
      render: (value, row) => {
        if (row.role) {
          return (
            <div className="flex items-center">
              <Shield className="w-4 h-4 mr-2 text-blue-500" />
              <span>{row.role.name}</span>
            </div>
          );
        } else if (row.permissions && row.permissions.length > 0) {
          return (
            <div className="flex items-center">
              <Key className="w-4 h-4 mr-2 text-purple-500" />
              <span>{row.permissions.length} permissions</span>
            </div>
          );
        }
        return '-';
      }
    },
    {
      key: 'status',
      label: 'Status',
      render: (value, row) => getStatusBadge(row.status, row.status_text)
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (value) => new Date(value).toLocaleDateString()
    },
    {
      key: 'expires_at',
      label: 'Expires',
      render: (value) => value ? new Date(value).toLocaleDateString() : 'Never'
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, row) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => openViewModal(row)}
            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
            title="View"
          >
            <Eye className="w-4 h-4" />
          </button>
          {(row.status === 'pending' || row.status === 0 || row.status === '0' || row.status === 2 || row.status === '2') && (
            <>
              <button
                onClick={() => handleResendInvitation(row)}
                className="p-1 text-green-600 hover:bg-green-50 rounded"
                title="Resend"
              >
                <Send className="w-4 h-4" />
              </button>
              <button
                onClick={() => openEditModal(row)}
                className="p-1 text-gray-600 hover:bg-gray-50 rounded"
                title="Edit"
              >
                <Edit className="w-4 h-4" />
              </button>
            </>
          )}
          <button
            onClick={() => openDeleteModal(row)}
            className="p-1 text-red-600 hover:bg-red-50 rounded"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )
    }
  ];

  const filterConfig = [
    {
      type: 'select',
      label: 'View Mode',
      value: viewMode,
      onChange: (value) => {
        setViewMode(value);
        setSelectedDataroom('');
        setPagination(prev => ({ ...prev, current_page: 1 }));
      },
      options: [
        { value: 'all', label: 'All Invitations' },
        { value: 'dataroom', label: 'By Dataroom' }
      ]
    },
    ...(viewMode === 'dataroom' ? [
      {
        type: 'select',
        label: 'Dataroom',
        value: selectedDataroom,
        onChange: (value) => {
          setSelectedDataroom(value);
          setPagination(prev => ({ ...prev, current_page: 1 }));
        },
        options: [
          { value: '', label: 'Select Dataroom' },
          ...datarooms.map(d => ({ value: d.uuid, label: d.name }))
        ]
      }
    ] : []),
    {
      type: 'select',
      label: 'Status',
      value: selectedStatus,
      onChange: (value) => {
        setSelectedStatus(value);
        setPagination(prev => ({ ...prev, current_page: 1 }));
      },
      options: [
        { value: '', label: 'All Status' },
        { value: 'pending', label: 'Pending' },
        { value: 'accepted', label: 'Accepted' },
        { value: 'expired', label: 'Expired' },
        { value: 'declined', label: 'Declined' }
      ]
    }
  ];

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Super Admin - Invitations</h1>
        <CreateButton
          onClick={() => setShowCreateModal(true)}
          label="Create Invitation"
        />
      </div>

      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        filters={filterConfig}
        placeholder="Search invitations by email..."
      />

      <EnhancedDataTable
        columns={columns}
        data={invitations}
        pagination={pagination}
        onPageChange={(page) => setPagination(prev => ({ ...prev, current_page: page }))}
        loading={loading}
        emptyMessage="No invitations found"
      />

      {/* Create Modal */}
      <ResponsiveModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          resetForm();
        }}
        title="Create Invitation"
      >
        <form onSubmit={handleCreateInvitation} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Dataroom *
            </label>
            <SearchableSelect
              value={formData.dataroom_uuid}
              onChange={(value) => setFormData(prev => ({ ...prev, dataroom_uuid: value }))}
              options={datarooms}
              optionLabel="name"
              optionValue="uuid"
              placeholder="Select a dataroom"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value.toLowerCase() }))}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:border-transparent focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="Enter email address"
              required
            />
          </div>

          {formData.dataroom_uuid && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Access Type
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="permission"
                      checked={selectionType === 'permission'}
                      onChange={(e) => {
                        setSelectionType(e.target.value);
                        setFormData(prev => ({ ...prev, dataroom_role_id: '', permission_ids: [] }));
                      }}
                      className="mr-2"
                    />
                    <span>Select Permissions</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="role"
                      checked={selectionType === 'role'}
                      onChange={(e) => {
                        setSelectionType(e.target.value);
                        setFormData(prev => ({ ...prev, dataroom_role_id: '', permission_ids: [] }));
                      }}
                      className="mr-2"
                    />
                    <span>Select Role</span>
                  </label>
                </div>
              </div>

              {selectionType === 'role' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Role
                  </label>
                  <SearchableSelect
                    value={formData.dataroom_role_id}
                    onChange={(value) => setFormData(prev => ({ ...prev, dataroom_role_id: value }))}
                    options={roles}
                    optionLabel="name"
                    optionValue="id"
                    placeholder="Select a role"
                  />
                </div>
              )}

              {selectionType === 'permission' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Permissions
                  </label>
                  <MultiSelectSearchable
                    value={formData.permission_ids}
                    onChange={(value) => setFormData(prev => ({ ...prev, permission_ids: value }))}
                    options={permissions.map(perm => ({
                      ...perm,
                      display_name: perm.display_name || perm.name
                    }))}
                    optionLabel="display_name"
                    optionValue="id"
                    placeholder="Select permissions"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tags
                </label>
                <MultiSelectSearchable
                  value={formData.tag_ids}
                  onChange={(value) => setFormData(prev => ({ ...prev, tag_ids: value }))}
                  options={tags}
                  optionLabel="name"
                  optionValue="id"
                  placeholder="Select tags (optional)"
                />
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Personal Message
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="3"
              placeholder="Add a personal message (optional)"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Expiration Date
            </label>
            <input
              type="datetime-local"
              value={formData.expires_at}
              onChange={(e) => setFormData(prev => ({ ...prev, expires_at: e.target.value }))}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:border-transparent focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <ResponsiveButton
              variant="secondary"
              onClick={() => {
                setShowCreateModal(false);
                resetForm();
              }}
              type="button"
            >
              Cancel
            </ResponsiveButton>
            <ResponsiveButton
              variant="primary"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Invitation'}
            </ResponsiveButton>
          </div>
        </form>
      </ResponsiveModal>

      {/* Edit Modal */}
      <ResponsiveModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          resetForm();
        }}
        title="Edit Invitation"
      >
        <form onSubmit={handleUpdateInvitation} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value.toLowerCase() }))}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:border-transparent focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="Enter email address"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Access Type
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="permission"
                  checked={selectionType === 'permission'}
                  onChange={(e) => {
                    setSelectionType(e.target.value);
                    setFormData(prev => ({ ...prev, dataroom_role_id: '', permission_ids: [] }));
                  }}
                  className="mr-2"
                />
                <span>Select Permissions</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="role"
                  checked={selectionType === 'role'}
                  onChange={(e) => {
                    setSelectionType(e.target.value);
                    setFormData(prev => ({ ...prev, dataroom_role_id: '', permission_ids: [] }));
                  }}
                  className="mr-2"
                />
                <span>Select Role</span>
              </label>
            </div>
          </div>

          {selectionType === 'role' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role
              </label>
              <SearchableSelect
                value={formData.dataroom_role_id}
                onChange={(value) => setFormData(prev => ({ ...prev, dataroom_role_id: value }))}
                options={roles}
                optionLabel="name"
                optionValue="id"
                placeholder="Select a role"
              />
            </div>
          )}

          {selectionType === 'permission' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Permissions
              </label>
              <MultiSelectSearchable
                value={formData.permission_ids}
                onChange={(value) => setFormData(prev => ({ ...prev, permission_ids: value }))}
                options={permissions.map(perm => ({
                  ...perm,
                  display_name: perm.display_name || perm.name
                }))}
                optionLabel="display_name"
                optionValue="id"
                placeholder="Select permissions"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tags
            </label>
            <MultiSelectSearchable
              value={formData.tag_ids}
              onChange={(value) => setFormData(prev => ({ ...prev, tag_ids: value }))}
              options={tags}
              optionLabel="name"
              optionValue="id"
              placeholder="Select tags (optional)"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Personal Message
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="3"
              placeholder="Add a personal message (optional)"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Expiration Date
            </label>
            <input
              type="datetime-local"
              value={formData.expires_at}
              onChange={(e) => setFormData(prev => ({ ...prev, expires_at: e.target.value }))}
              className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:border-transparent focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <ResponsiveButton
              variant="secondary"
              onClick={() => {
                setShowEditModal(false);
                resetForm();
              }}
              type="button"
            >
              Cancel
            </ResponsiveButton>
            <ResponsiveButton
              variant="primary"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Updating...' : 'Update Invitation'}
            </ResponsiveButton>
          </div>
        </form>
      </ResponsiveModal>

      {/* View Modal */}
      <ResponsiveModal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedInvitation(null);
        }}
        title="Invitation Details"
      >
        {selectedInvitation && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-500">Email</label>
              <p className="mt-1 text-sm text-gray-900">{selectedInvitation.email}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Dataroom</label>
              <p className="mt-1 text-sm text-gray-900">
                {selectedInvitation.dataroom?.name || '-'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500">Status</label>
              <div className="mt-1">
                {getStatusBadge(selectedInvitation.status, selectedInvitation.status_text)}
              </div>
            </div>

            {selectedInvitation.role && (
              <div>
                <label className="block text-sm font-medium text-gray-500">Role</label>
                <p className="mt-1 text-sm text-gray-900">{selectedInvitation.role.name}</p>
              </div>
            )}

            {selectedInvitation.permissions && selectedInvitation.permissions.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-500">Permissions</label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {selectedInvitation.permissions.map(perm => (
                    <span key={perm.id} className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded">
                      {perm.display_name || perm.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {selectedInvitation.tags && selectedInvitation.tags.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-500">Tags</label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {selectedInvitation.tags.map(tag => (
                    <span key={tag.id} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">
                      {tag.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {selectedInvitation.message && (
              <div>
                <label className="block text-sm font-medium text-gray-500">Message</label>
                <p className="mt-1 text-sm text-gray-900">{selectedInvitation.message}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-500">Created At</label>
              <p className="mt-1 text-sm text-gray-900">
                {new Date(selectedInvitation.created_at).toLocaleString()}
              </p>
            </div>

            {selectedInvitation.expires_at && (
              <div>
                <label className="block text-sm font-medium text-gray-500">Expires At</label>
                <p className="mt-1 text-sm text-gray-900">
                  {new Date(selectedInvitation.expires_at).toLocaleString()}
                </p>
              </div>
            )}

            {selectedInvitation.accepted_at && (
              <div>
                <label className="block text-sm font-medium text-gray-500">Accepted At</label>
                <p className="mt-1 text-sm text-gray-900">
                  {new Date(selectedInvitation.accepted_at).toLocaleString()}
                </p>
              </div>
            )}

            <div className="flex justify-end pt-4">
              <ResponsiveButton
                variant="secondary"
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedInvitation(null);
                }}
              >
                Close
              </ResponsiveButton>
            </div>
          </div>
        )}
      </ResponsiveModal>

      {/* Delete Confirmation Modal */}
      <ResponsiveModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedInvitation(null);
        }}
        title="Delete Invitation"
      >
        <div className="space-y-4">
          <div className="flex items-center space-x-3 text-red-600">
            <AlertCircle className="w-5 h-5" />
            <p>Are you sure you want to delete this invitation?</p>
          </div>

          {selectedInvitation && (
            <div className="bg-gray-50 p-3 rounded">
              <p className="text-sm text-gray-600">
                Email: <span className="font-medium text-gray-900">{selectedInvitation.email}</span>
              </p>
              <p className="text-sm text-gray-600">
                Dataroom: <span className="font-medium text-gray-900">
                  {selectedInvitation.dataroom?.name || '-'}
                </span>
              </p>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <ResponsiveButton
              variant="secondary"
              onClick={() => {
                setShowDeleteModal(false);
                setSelectedInvitation(null);
              }}
            >
              Cancel
            </ResponsiveButton>
            <ResponsiveButton
              variant="danger"
              onClick={handleDeleteInvitation}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Deleting...' : 'Delete'}
            </ResponsiveButton>
          </div>
        </div>
      </ResponsiveModal>
    </div>
  );
};

export default SuperAdminInvitations;